import { autoUpdate, flip, FloatingPortal, offset, shift, size, useFloating } from "@floating-ui/react"
import clsx from "clsx"
import { Component, type ReactNode } from "react"
import { Button } from "@/shared/components"
import { gt } from "@/shared/config/i18n"
import { currLang, daysOfWeek, Months } from "@/shared/config/locales.ts"
import type { DateRange } from "@/shared/types/index.ts"
import { getCalendarDates, getMonthName, getRangeClasses } from "@/shared/utils"
import { AwayListener } from "../popper/AwayListener.tsx"
import "./style.scss"
import { useTranslation } from "react-i18next"
import { Calendar03, Close, SelectArrow } from "@/shared/components/ui/icons"

interface ICalendarProps {
  onAction: (action: "prev" | "next") => void
  onDateClick?: (date: Date) => void
  date?: Date
  value?: Date | DateRange
  filter?: (date: Date) => boolean
  showOut?: boolean
}

interface DatePickerFloatProps {
  target: ReactNode
  children: ReactNode
  showClear?: boolean
  onClear?: VoidFunction
  label?: string
  error?: string
  required?: boolean
  wrapperClass?: string
  show?: boolean
  onShowChange: (show: boolean) => void
}

export interface SinglePickerProps {
  value?: Date
  onSelect?: (date?: Date) => void
  filter?: (date: Date) => boolean
  required?: boolean
}

export type RangePickerProps = {
  value?: (Date | null)[]
  monthsShow?: number
  onSelect?: (range: (Date | null)[]) => void
  filter?: (date: Date) => boolean
  onComplete?: (range: (Date | null)[]) => void
}

interface CalendarState {
  showYears: boolean
  showMonth: boolean
  yearPage: number
  date?: Date
}

class Calendar extends Component<ICalendarProps, CalendarState> {
  static RangePicker: typeof RangePicker
  static SinglePicker: typeof SinglePicker
  static Float: typeof DatePickerFloat
  date: Date = new Date(Date.now())

  constructor(props: ICalendarProps) {
    super(props)
    this.state = {
      showYears: false,
      showMonth: false,
      yearPage: 0,
      date: props.date ?? new Date(Date.now())
    }
  }

  get allDates() {
    return getCalendarDates(this.state.date ?? new Date(Date.now()))
  }

  get currentYear() {
    return this.state.date?.getFullYear() ?? new Date().getFullYear()
  }

  get years() {
    const { yearPage } = this.state
    const startYear = this.currentYear + yearPage * 12 - 6
    return Array.from({ length: 12 }, (_, index) => startYear + index)
  }

  handlePrevYearPage = () => {
    this.setState(prevState => ({ yearPage: prevState.yearPage - 1 }))
  }

  handleNextYearPage = () => {
    this.setState(prevState => ({ yearPage: prevState.yearPage + 1 }))
  }

  toggleYearView = () => {
    this.setState(prevState => ({ showYears: !prevState.showYears, showMonth: false }))
  }

  handleYearSelect = (year: number) => {
    const currentDate = this.state.date ?? new Date(Date.now())
    this.setState({ showYears: false, yearPage: 0, date: new Date(currentDate.setFullYear(year)), showMonth: true })
  }
  handleMonthSelect = (month: number) => {
    const currentDate = this.props.date ?? new Date(Date.now())
    this.setState({ date: new Date(currentDate.setMonth(month)), showMonth: false })
  }

  componentDidUpdate(prevProps: Readonly<ICalendarProps>, prevState: Readonly<CalendarState>) {
    if (prevProps.date !== this.props.date) this.setState({ ...prevState, date: this.props.date })
  }

  onTodayClick() {
    this.setState({ date: new Date(Date.now()), showYears: false, yearPage: 0 })
  }

  render() {
    const { onAction, onDateClick, filter, value, showOut = true } = this.props
    const { showYears, showMonth } = this.state
    return (
      <>
        <div className="calendar__control">
          <div className="calendar__control-prev" onClick={() => (showYears ? this.handlePrevYearPage() : onAction("prev"))}>
            <SelectArrow className="rotate-90" />
          </div>
          <div className="calendar__control-month cursor-pointer capitalize hover:underline" onClick={this.toggleYearView}>
            {getMonthName(this.state.date)} {this.state.date?.getFullYear()}
          </div>
          <div className="calendar__control-next" onClick={() => (showYears ? this.handleNextYearPage() : onAction("next"))}>
            <SelectArrow className="-rotate-90" />
          </div>
        </div>
        {Boolean(!showYears && !showMonth) && (
          <>
            <div className="grid w-full grid-cols-7">
              {daysOfWeek.map(name => (
                <div key={name.value} className="calendar__week-name">
                  {name.meta?.init[currLang]}
                </div>
              ))}
            </div>
            <div className="grid w-full grid-cols-7 gap-y-[3px]">
              {this.allDates.map(({ isOut, date, isToday }) => {
                const isDisabled = filter ? !filter(date) : isOut && !showOut
                return (
                  <div
                    key={date.getTime()}
                    className={clsx(
                      "calendar__date",
                      isToday && "calendar__today",
                      (isOut || isDisabled) && "calendar__outDate",
                      getRangeClasses(date, value)
                    )}
                    onClick={() => !isDisabled && onDateClick?.(date)}
                  >
                    {!showOut && isOut ? "" : date.getDate()}
                  </div>
                )
              })}
            </div>
          </>
        )}
        {showYears && (
          <div className="grid grid-cols-3 gap-2">
            {this.years.map(year => (
              <div
                key={year}
                className={clsx("calendar__year", year === this.currentYear && "calendar__today font-bold")}
                onClick={() => this.handleYearSelect(year)}
              >
                {year}
              </div>
            ))}
          </div>
        )}
        {showMonth && (
          <div className="grid grid-cols-3 gap-2">
            {Months.map(month => {
              return (
                <div
                  key={month.value}
                  className={clsx("calendar__month", month.value - 1 === this.state.date?.getMonth() && "calendar__today font-bold")}
                  onClick={this.handleMonthSelect.bind(this, month.value - 1)}
                >
                  {month.labels[currLang]}
                </div>
              )
            })}
          </div>
        )}
        {(this.state.date?.getMonth() !== new Date(Date.now()).getMonth() || showYears) && (
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              onDateClick?.(new Date())
              this.onTodayClick.bind(this)
            }}
            className="mt-2 w-full"
          >
            {gt("today")}
          </Button>
        )}
      </>
    )
  }
}

interface RangePickerState {
  current: Date
}

class RangePicker extends Component<RangePickerProps, RangePickerState> {
  constructor(props: RangePickerProps) {
    super(props)
    this.state = {
      current: new Date(Date.now())
    }
  }

  get allDates() {
    const { monthsShow = 1 } = this.props
    const { current } = this.state
    let counter = 0 - (monthsShow === 2 ? 0 : Math.floor(monthsShow / 2))
    return Array.from({ length: monthsShow }).map(() => {
      return new Date(current.getFullYear(), current.getMonth() + counter++)
    })
  }

  addRange = (date: Date) => {
    const { value, onSelect, onComplete } = this.props
    let result: (Date | null)[] = []
    const range = value!
    if (range?.[0] != null && range?.[1] != null) {
      result = [date, null]
    } else if (range?.[0] != null) {
      result = date < range[0] ? [date, range[0]] : [range[0], date]
    } else {
      result = [date, null]
    }

    if (result.every(date => date != null)) {
      onComplete?.(result)
    }
    onSelect?.(result)
  }

  onCalendarAction = (action: "prev" | "next") => {
    this.setState(prevState => {
      const newDate = new Date(prevState.current)
      newDate.setMonth(newDate.getMonth() + (action === "prev" ? -1 : 1))
      return { current: newDate }
    })
  }

  render() {
    const { value } = this.props

    return (
      <div className="datepicker">
        {this.allDates.map(date => (
          <div key={date.getTime()} className="calendar">
            <Calendar showOut={false} onDateClick={this.addRange} onAction={this.onCalendarAction} value={value} date={date} />
          </div>
        ))}
      </div>
    )
  }
}

interface SinglePickerState {
  current: Date
}

class SinglePicker extends Component<SinglePickerProps, SinglePickerState> {
  constructor(props: SinglePickerProps) {
    super(props)
    this.state = {
      current: new Date()
    }
  }

  componentDidMount() {
    if (this.props.value) {
      this.setState({ current: this.props.value })
    }
  }

  componentDidUpdate(prevProps: SinglePickerProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ current: this.props.value ?? new Date() })
    }
  }

  onCalendarAction = (action: "prev" | "next") => {
    this.setState(prevState => {
      const newCurrent = new Date(prevState.current)
      newCurrent.setMonth(newCurrent.getMonth() + (action === "prev" ? -1 : 1))
      return { current: newCurrent }
    })
  }

  render() {
    const { value, onSelect, filter } = this.props
    const { current } = this.state

    return (
      <div className="datepicker">
        <div className="calendar">
          <Calendar onDateClick={onSelect} onAction={this.onCalendarAction} value={value} date={current} filter={filter} />
        </div>
      </div>
    )
  }
}

const DatePickerFloat = ({
  onShowChange,
  show,
  label,
  required,
  error,
  showClear,
  onClear,
  target,
  children,
  wrapperClass
}: DatePickerFloatProps) => {
  const { t } = useTranslation("errors")
  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    open: show,
    onOpenChange: onShowChange,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      shift(),
      size({
        padding: 10
      })
    ]
  })

  return (
    <AwayListener onAway={() => onShowChange(false)}>
      <div className="relative flex flex-col gap-2">
        {label != null && (
          <label htmlFor="" className={"text-[13px]" + (error ? " text-red-500" : "")}>
            {label} {required && <strong className="text-[10px] text-red-500"> ＊</strong>}
          </label>
        )}
        <div
          className={clsx(
            "flex w-full items-center justify-between gap-8 rounded-lg border border-border bg-paper px-4 py-2",
            show && "border-success-main",
            error != null && "!border-red-500",
            wrapperClass
          )}
          ref={refs.setReference}
          tabIndex={0}
          role="button"
          aria-haspopup="dialog"
          aria-label={typeof label === "string" ? label : undefined}
          onClick={() => onShowChange(!show)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onShowChange(!show)
            }
          }}
        >
          {target}
          <div className="absolute right-3 flex items-center gap-2">
            {showClear ? (
              <div
                className="cursor-pointer rounded-full p-0.5 hover:bg-slate-300 hover:text-black"
                tabIndex={0}
                role="button"
                aria-label="Clear"
                onClick={e => {
                  e.stopPropagation()
                  onClear?.()
                  onShowChange(true)
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    e.stopPropagation()
                    onClear?.()
                    onShowChange(true)
                  }
                }}
              >
                <Close size={20} />
              </div>
            ) : (
              <Calendar03 size={20} />
            )}
          </div>
        </div>
        <span className="field-label__error mt-1 text-red-600 text-xs">{t(error)}</span>
      </div>
      {show && (
        <FloatingPortal>
          <div
            onMouseDown={e => e.stopPropagation()}
            ref={refs.setFloating}
            className="z-[1110000] rounded-[12px] border border-border bg-paper p-3"
            style={{ ...floatingStyles }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </AwayListener>
  )
}

Calendar.RangePicker = RangePicker
Calendar.SinglePicker = SinglePicker
Calendar.Float = DatePickerFloat
export default Calendar
